import { Component, computed, effect, inject, input, output, ViewChild } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { UiKitModule } from "@app/ui-kit.module";
import { FullProductType, LocalizeProductNamePipe, Product } from "@domain//product/product";
import { ProductService } from "@domain/product/product.service";
import { UserService } from "@domain/user/user.service";
import { CheckBoxControl } from "@odx/angular/cdk/checkbox-control";
import { map } from "rxjs";

@Component({
  selector: "app-attachment-card",
  templateUrl: "./attachment-card.component.html",
  styleUrls: ["./attachment-card.component.scss"],
  imports: [UiKitModule, LocalizeProductNamePipe],
  standalone: true,
})
export class AttachmentCardComponent {
  private readonly localizeProductName = inject(LocalizeProductNamePipe);
  private readonly productService = inject(ProductService);
  private readonly userService = inject(UserService);

  readonly attachmentData = input.required<Product>();
  readonly showCosts = input.required<boolean>();
  readonly selectionMode = input.required<"single" | "multi">();
  readonly quantity = input<number>(0);
  readonly preselectedQuantity = input<number | null>(null);

  readonly quantityChanged$ = output<number>();

  protected readonly attachment = computed(() => this.attachmentData());
  private readonly showZrefPrices = toSignal(this.userService.user$.pipe(map((user) => user.showZrefPrices)), {
    initialValue: false,
  });
  protected readonly priceData = computed(() => {
    const showZref = this.showZrefPrices();
    const regularPrice = this.productService.getPriceData(this.attachment());
    const zrefPrice = this.productService.getZrefPriceData(this.attachment());
    if (showZref) {
      return zrefPrice;
    }
    return regularPrice;
  });
  protected readonly isGuide = computed(() => this.attachment().fullType === FullProductType.ATTACHMENT_INSTRUCTIONS);
  protected readonly Infinity = Infinity;

  constructor() {
    effect(() => {
      // trigger form selection once if preselected value
      const quantity = this.preselectedQuantity();
      if (quantity) {
        this.quantityChanged$.emit(quantity);
      }
    });
  }

  @ViewChild("checkboxAttachment")
  protected checkboxAttachment?: CheckBoxControl;

  protected isTooltipDisabled(): boolean {
    return this.localizeProductName.transform(this.attachment()).length <= 80;
  }

  protected onCardClicked() {
    if (this.checkboxAttachment) {
      this.quantityChanged$.emit(this.checkboxAttachment.checked ? 0 : 1);
    }
  }

  protected onCheckedChanged(checked: boolean) {
    this.quantityChanged$.emit(checked ? 1 : 0);
  }

  protected onQuantityChanged(quantity: number) {
    if (quantity !== this.quantity()) {
      this.quantityChanged$.emit(quantity);
    }
  }
}
